import React from 'react';
import { Button, FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { FieldPath, UseFormReturn } from 'react-hook-form';
import { useParams } from 'react-router';
import { StyledSectionTitle } from '../PlusWebsiteEditor/PlusWebsiteEditor.styles';
import { ColumnWithGap } from '../PlusWebsiteEditor/sections/HeroBlock/HeroBlock.styled';
import { StyledSeoNote } from './SeoLockedContent';
import DateEditor from '../FormEditors/DateEditor';
import { ClinicalReview, ClinicWithConditionsAndPromo } from './types';
import { nextClinicalReview, reviewerLabel, today } from './clinicalReview';
import useGetPrescribers from './ModifyClinic/hooks/useGetPrescribers';

type ClinicFieldName = FieldPath<ClinicWithConditionsAndPromo>;

interface ClinicalReviewEditorProps {
  form: UseFormReturn<ClinicWithConditionsAndPromo>;
  // The promotional content the review belongs to: the clinic's or one condition's.
  name: string;
  pageLabel: string;
  page: string;
  isDisabled?: boolean;
}

const NO_REVIEWER = '';

// Only rendered on an existing clinic's page, whose route carries the clinic id.
function ClinicalReviewEditor({ form, name, pageLabel, page, isDisabled }: ClinicalReviewEditorProps) {
  const { clinicId } = useParams<{ clinicId: string }>();
  const { prescribers, isLoading } = useGetPrescribers(clinicId);
  const reviewName = `${name}.clinical_review` as ClinicFieldName;
  const review = form.watch(reviewName) as ClinicalReview | null | undefined;
  const reviewerIsGone = Boolean(
    review && !isLoading && !prescribers.some((prescriber) => prescriber._id === review.prescriber_id)
  );

  const setReview = (next: ClinicalReview | null) => {
    form.setValue(reviewName, next as never, { shouldDirty: true });
  };

  const handleReviewerChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setReview(nextClinicalReview(review, String(event.target.value), today()));
  };

  const markReviewedToday = () => {
    if (review) setReview({ ...review, reviewed_at: today() });
  };

  return (
    <ColumnWithGap>
      <StyledSectionTitle>Clinical review</StyledSectionTitle>
      <StyledSeoNote>
        Shown as &quot;{pageLabel}&quot; with the review date on {page}. Picking or changing the reviewer dates the
        review today; editing anything else never moves the date.
      </StyledSeoNote>
      {!isLoading && prescribers.length === 0 && (
        <StyledSeoNote>Add prescribers on the Prescribers tab to pick a reviewer.</StyledSeoNote>
      )}
      <FormControl fullWidth>
        <InputLabel shrink>Reviewed by</InputLabel>
        <Select value={review?.prescriber_id ?? NO_REVIEWER} onChange={handleReviewerChange} disabled={isDisabled} displayEmpty>
          <MenuItem value={NO_REVIEWER}>No reviewer</MenuItem>
          {reviewerIsGone && review && (
            <MenuItem value={review.prescriber_id} disabled>
              No longer a prescriber of this clinic
            </MenuItem>
          )}
          {prescribers.map((prescriber) => (
            <MenuItem key={prescriber._id} value={prescriber._id}>
              {reviewerLabel(prescriber, prescribers)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {reviewerIsGone && (
        <StyledSeoNote>This reviewer is no longer a prescriber of this clinic. Pick another reviewer or clear the review.</StyledSeoNote>
      )}
      {review && (
        <>
          <DateEditor
            control={form.control}
            name={`${reviewName}.reviewed_at` as ClinicFieldName}
            id={`${name}.clinical_review.reviewed_at`.replace(/\./g, '-')}
            label="Last reviewed on"
            disabled={isDisabled}
          />
          <div>
            <Button type="button" variant="outlined" onClick={markReviewedToday} disabled={isDisabled}>
              Mark as reviewed today
            </Button>
          </div>
        </>
      )}
    </ColumnWithGap>
  );
}

export default ClinicalReviewEditor;
